<template>
	<AppDialog :dialogName="'Dynamic Data Mapper'" width="600">
		<template v-slot:header>
			<DialogHeader :service="service" />
		</template>
		<div class="dynamic-data-mapper-dialog px-10 py-4">
			<div class="title-text">Give your property a generic name</div>
			<div class="description-text mt-4">
				This property may have a different name in your users account. Give it a generic name anyone can understand so they can map it to
				their corresponding property. Ex: “Lead_number” can become “Phone number”
			</div>
			<div class="flex-space-between--center my-9">
				<div class="flex-1 key-text">
					<div class="param-title">Name in your account</div>
					<BasicTextField class="key noselect" disabled :value="originalKey" />
				</div>
				<SIcon class="mx-4 mt-4" name="mapper-arrow-divider" width="37" />
				<div class="flex-1 display-name-input">
					<div class="param-title">Generic name anyone can understand</div>
					<BasicTextField v-model="displayName" />
				</div>
			</div>
			<div class="pb-4 mt-9">
				<AnemicButton :disabled="!displayName && !displayName.length > 0" class="display-flex center-margin" @click="save">
					Save
				</AnemicButton>
			</div>
		</div>
	</AppDialog>
</template>

<script>
import cloneDeep from 'lodash/cloneDeep';
import { mapActions, mapGetters } from 'vuex';
import AppDialog from '../AppDialog/AppDialog';
import DialogHeader from './common/DialogHeader';
import WidgetService from '@/utils/WidgetService';
import AppDialogManagerService from '../AppDialogManager/app-dialog-manager-service';
import { saveConfigurationFieldResponse } from '@/store/modules/workflowConfigurations/actions';
import { getConfigurationFieldResponse } from '@/store/modules/workflowConfigurations/getters';
import { getConfigurationAndInterfaceIdByFieldUUID } from '@/store/modules/selectedWorkflow/modules/configurations/getters';
import { editedWorkflowVersion } from '@/store/modules/selectedWorkflow/getters';
import { saveMappingsInResponse } from '@/store/modules/selectedWorkflow/modules/userSchemaMapper/actions';
import { saveWidget } from '@/store/modules/widgets/actions';

export default {
	name: 'AddMappingToMapperDialog',
	components: {
		AppDialog,
		DialogHeader,
	},
	props: {
		service: { type: Object },
		originalKey: { type: String },
		widgetFieldUUID: { type: String },
		dialogPromiseHandlers: { type: Object },
	},
	computed: {
		...mapGetters([
			getConfigurationFieldResponse.fullPath,
			getConfigurationAndInterfaceIdByFieldUUID.fullPath,
			editedWorkflowVersion.fullPath,
		]),
		creatorMapResponse() {
			return this[getConfigurationFieldResponse.fullPath](this.configurationId, this.widgetFieldUUID) || {};
		},
		configurationAndInterfaceIdByFieldUUID() {
			return this[getConfigurationAndInterfaceIdByFieldUUID.fullPath](this.widgetFieldUUID);
		},
		configurationId() {
			return this.configurationAndInterfaceIdByFieldUUID.configurationId;
		},
		interfaceUUID() {
			return this.configurationAndInterfaceIdByFieldUUID.interfaceUUID;
		},
		interface() {
			return this.configurationAndInterfaceIdByFieldUUID.interface;
		},
	},
	methods: {
		...mapActions([saveConfigurationFieldResponse.fullPath, saveWidget.fullPath, saveMappingsInResponse.fullPath]),
		close() {
			this.dialogPromiseHandlers.resolve();
			AppDialogManagerService.closeCurrentDialog();
		},
		async save() {
			const value = { originalKey: this.originalKey, displayName: this.displayName };
			await this.saveMappingsInMapperWidgetField(value);
			await this.saveMappingsInResponse(value);
			this.close();
		},
		async saveMappingsInMapperWidgetField(value) {
			const currentInterface = cloneDeep(this.interface);
			await WidgetService.saveMappingsInMapperWidgetField(
				value,
				currentInterface,
				this[editedWorkflowVersion.fullPath]?.id,
				this.widgetFieldUUID,
				this[saveWidget.fullPath]
			);
		},
		async saveMappingsInResponse(value) {
			await this[saveMappingsInResponse.fullPath]({
				configurationId: this.configurationId,
				interfaceUUID: this.interfaceUUID,
				widgetFieldUUID: this.widgetFieldUUID,
				value,
				remove: false,
			});
		},
	},
	data() {
		return {
			displayName: '',
		};
	},
};
</script>

<style lang="scss" scoped>
.description-text {
	font-size: 12px;
	color: var(--signup-grey);
}
.dynamic-data-mapper-dialog {
	.title-text {
		font-size: 18px;
		font-weight: 600;
		color: var(--primary-font-color);
	}
	.param-title {
		font-size: 12px;
		color: #292829;
	}
}
</style>
